import { Injectable, Inject } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { ConfigurationService } from './configuration.service';

export interface SeoData {
  title: string;
  description: string;
  keywords?: string;
  robots?: string;
  image?: string;
}

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly SITE_NAME = 'Televisa Llega';
  private readonly DEFAULT_IMAGE = '/assets/images/website_general/logo-televisaunivisionllega.png';

  constructor(
    private titleService: Title,
    private meta: Meta,
    private router: Router,
    private configuration: ConfigurationService,
    @Inject(DOCUMENT) private doc: Document
  ) {}

  update(data: SeoData): void {
    const pageTitle = data.title ? `${data.title} | ${this.SITE_NAME}` : this.SITE_NAME;
    const url = this.configuration.webUrl + this.router.url;
    const image = data.image || (this.configuration.webUrl + this.DEFAULT_IMAGE);
    const robots = data.robots || 'index, follow';

    this.titleService.setTitle(pageTitle);

    this.meta.updateTag({ name: 'description', content: data.description });
    this.meta.updateTag({ name: 'robots', content: robots });

    if (data.keywords) {
      this.meta.updateTag({ name: 'keywords', content: data.keywords });
    }

    // Open Graph
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ property: 'og:description', content: data.description });
    this.meta.updateTag({ property: 'og:url', content: url });
    this.meta.updateTag({ property: 'og:image', content: image });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: this.SITE_NAME });

    // Twitter Card
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
    this.meta.updateTag({ name: 'twitter:description', content: data.description });
    this.meta.updateTag({ name: 'twitter:image', content: image });

    this.setCanonical(url);
  }

  private setCanonical(url: string): void {
    let link: HTMLLinkElement = this.doc.querySelector('link[rel="canonical"]');
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }
}
